import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, Package, Check, X, Save, Loader2 } from 'lucide-react';

interface Material {
  id: string;
  name: string;
  category: string;
  photo_url?: string;
}

interface MaterialCheckItem {
  material_type_id: string;
  status: 'PRESENT' | 'ABSENT';
  photo_url?: string;
  notes?: string;
}

export default function MaterialCheck({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [checks, setChecks] = useState<Record<string, MaterialCheckItem>>({});
  const [saved, setSaved] = useState(false);

  const { data: materials, isLoading } = useQuery<Material[]>({
    queryKey: ['materials'],
    queryFn: () => api.get('/materials').then(res => res.data.data),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const items = Object.entries(checks).map(([material_id, check]) => ({
        material_type_id: material_id,
        status: check.status,
        notes: check.notes,
        photo_url: check.photo_url,
      }));
      await api.post('/materials/checks/batch', {
        visit_id: visitId,
        store_id: storeId,
        items,
      });
    },
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
      setTimeout(() => setLocation(`/visit/${storeId}`), 1500);
    },
  });

  const toggleCheck = (materialId: string, isPresent: boolean) => {
    setChecks((prev) => ({
      ...prev,
      [materialId]: {
        ...(prev[materialId] || {}),
        material_type_id: materialId,
        status: isPresent ? 'PRESENT' as const : 'ABSENT' as const,
      },
    }));
  };

  const checkedCount = Object.values(checks).filter((c) => c.status === 'PRESENT').length;
  const totalChecked = Object.keys(checks).length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button onClick={() => setLocation(`/visit/${storeId}`)} className="p-2 -ml-2 rounded-full hover:bg-white/20">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Materiais PDV</h1>
            <p className="text-sm text-white/80">Verificar materiais de marketing</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{checkedCount}/{materials?.length || 0}</p>
            <p className="text-xs text-white/70">presentes</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 space-y-3">
        {saved && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800 font-medium">Verificação salva com sucesso!</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !materials || materials.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Package className="h-12 w-12 mb-3 opacity-40" />
            <p className="text-sm font-medium">Nenhum material cadastrado</p>
            <p className="text-xs mt-1">Cadastre tipos de material no painel administrativo</p>
          </div>
        ) : (
          materials.map((material) => {
            const check = checks[material.id];
            return (
              <div key={material.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{material.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{material.category}</p>
                  </div>
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => toggleCheck(material.id, true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                      check?.status === 'PRESENT'
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <Check className="h-4 w-4" />
                    Presente
                  </button>
                  <button
                    onClick={() => toggleCheck(material.id, false)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                      check?.status === 'ABSENT'
                        ? 'bg-red-100 text-red-700 border-2 border-red-300'
                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <X className="h-4 w-4" />
                    Ausente
                  </button>
                </div>
              </div>
            );
          })
        )}
      </main>

      {totalChecked > 0 && (
        <div className="p-4 bg-white border-t border-gray-200">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || saved}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            {saveMutation.isPending ? 'Salvando...' : 'Salvar Verificação'}
          </button>
        </div>
      )}
    </div>
  );
}
